import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { LoadingState } from '@atlas/base';
import { BonusTitle, QuickBonus, Slider } from '@atlas/components';
import { AccountManager, useSettings } from '@atlas/core';
import { Icon, Label, Loader, ToggleCheckbox } from '@atlas/ui';

import useBonusesService from 'hooks/useBonusesService';
import useBonusSliderAtEnd from 'hooks/useBonusSliderAtEnd';
import { BASE_PATH, QUICK_DEPOSIT_POPUP_TITLE_TYPE } from 'src/common/constants';
import { CDN_TYPE_CRM, imageUrlCDN } from 'src/common/utils';
import { useBonusesContext } from 'src/components/BonusesProvider';
import { getMinDeposit, isAmountBelowMinDeposit } from 'src/components/BonusesProvider/utils';

const TITLE = {
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT]: 'quick_deposit_get_bonus',
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT]: 'insufficient_funds_get_bonus',
};
const defaultImage = `${BASE_PATH}/static/assets/images/gift.png`;

const QuickDepositBonuses = ({
  currencyFormatter,
  isRtl = false,
  titleType = QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
}) => {
  const bonusesService = useBonusesService();
  const { lang } = useSettings();
  const currency = AccountManager.getAccountCurrency();
  const {
    amountToGetBonus,
    bonusesList,
    depositAmount,
    isLockedBonuses,
    isValidatingBonus,
    paymentMethod,
    selectedBonus,
    setSelectedBonus,
  } = useBonusesContext();

  const sliderKey = useMemo(() => bonusesList.map(({ id }) => id).join('-'), [bonusesList]);
  const {
    atEnd: bonusSliderAtEnd,
    onSwiper: onBonusSwiper,
    scrollToSlide,
    syncSwiperAtEnd,
  } = useBonusSliderAtEnd(sliderKey);
  const selectedBonusId = selectedBonus?.id;

  const selectedBonusIndex = useMemo(
    () => bonusesList.findIndex(bonus => bonus.id === selectedBonusId),
    [bonusesList, selectedBonusId],
  );
  const dir = isRtl ? 'rtl' : 'ltr';
  const hasSingleBonus = bonusesList.length === 1;
  const titleMessage = TITLE[titleType] ?? TITLE[QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT];

  useEffect(() => {
    if (hasSingleBonus || !selectedBonusId || !paymentMethod?.id) {
      return;
    }

    if (selectedBonusIndex >= 0) {
      scrollToSlide(selectedBonusIndex, { force: true });
    }
  }, [hasSingleBonus, paymentMethod?.id, scrollToSlide, selectedBonusId, selectedBonusIndex]);

  const handleSelectBonus = useCallback(
    bonus => {
      const isActive = selectedBonus?.id === bonus.id;
      const newActiveBonus = isActive ? null : bonus;
      setSelectedBonus(newActiveBonus);
    },
    [selectedBonus, setSelectedBonus],
  );

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      className: 'deposit-bonus__slider',
      dir,
      loop: false,
      nextBtn: <Slider.NextBtn className="deposit-bonus-slider__next-arrow" />,
      onSlideChange: syncSwiperAtEnd,
      onSwiper: onBonusSwiper,
      prevBtn: <Slider.PrevBtn className="deposit-bonus-slider__prev-arrow" />,
      slidesPerView: 'auto',
      spaceBetween: 8,
    }),
    [dir, onBonusSwiper, syncSwiperAtEnd],
  );

  if (bonusesService?.loading === LoadingState.loading) {
    return <Loader className="profile-deposit__loader" />;
  }

  if (!bonusesList.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus?.financeDataList?.find(item => item.currency === currency);
    const freeSpinMinAmount = bonus?.freeSpinMinAmountsDeposit?.[currency] ?? bonus?.freeSpinAmount;
    const image = bonus?.getImage(lang, 'ENG');
    const bonusImage = imageUrlCDN(image, CDN_TYPE_CRM);

    const isActive = selectedBonus?.id === bonus.id;
    const isBelowMinDeposit = isAmountBelowMinDeposit(
      depositAmount,
      getMinDeposit(bonus, currency),
    );
    const isActiveSelection = isActive && !isValidatingBonus && !bonus?.disabled;
    const bonusTitle = (
      <BonusTitle
        className="quick-deposit-bonus-title"
        name={bonus?.name}
        nameHighlighted={bonus?.nameHighlighted}
      />
    );

    const bonusDescription = (
      <div className="quick-deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: currency || bonus?.currency,
            minBonusAmount: freeSpinMinAmount ?? finance?.minDeposit,
          }}
        />
      </div>
    );

    const disableBlock = (
      <div className="quick-deposit-bonus-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label className="disabled-title" message="deposit_bonus_unavaliable_message" />
      </div>
    );

    return (
      <button
        className={classNames('quick-deposit-bonus', {
          'quick-deposit-bonus--single': hasSingleBonus,
        })}
        key={bonus?.id}
        onClick={() => handleSelectBonus(bonus)}
        type="button"
      >
        <QuickBonus
          active={isActiveSelection}
          defaultImage={defaultImage}
          description={bonusDescription}
          disabled={bonus?.disabled || false}
          disabledBlock={disableBlock}
          image={bonusImage}
          title={bonusTitle}
          toggleCheckbox={
            <div className="quick-deposit-bonus-toggle">
              <ToggleCheckbox
                checked={isActiveSelection}
                disabled={
                  bonus?.disabled || isBelowMinDeposit || isLockedBonuses || isValidatingBonus
                }
                onChange={() => handleSelectBonus(bonus)}
              />
            </div>
          }
        />
      </button>
    );
  };

  return (
    <>
      {amountToGetBonus > 0 && (
        <Label
          className="quick-deposit-bonus-error"
          message="quick_deposit_add_amount_to_get_bonus"
          params={{
            amount: currencyFormatter.format(amountToGetBonus),
          }}
        />
      )}

      <div
        className={classNames('quick-deposit-wrapper', {
          single: hasSingleBonus,
        })}
      >
        {!hasSingleBonus && <Label className="slider-title" message={titleMessage} />}
        {hasSingleBonus ? (
          renderBonus(bonusesList[0])
        ) : (
          <div
            className={classNames('slider-wrapper', {
              'bonus-slider-outer--at-end': bonusSliderAtEnd,
              single: hasSingleBonus,
            })}
          >
            <Slider key={sliderKey} {...sliderConfig}>
              {bonusesList.map(bonus => (
                <Slider.Slide className="quick-deposit-slide" key={bonus.id}>
                  {renderBonus(bonus)}
                </Slider.Slide>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </>
  );
};

QuickDepositBonuses.propTypes = {
  currencyFormatter: PropTypes.shape({
    format: PropTypes.func.isRequired,
  }).isRequired,
  isRtl: PropTypes.bool,
  titleType: PropTypes.string,
};

export default QuickDepositBonuses;
